import { AttachFile, DeleteRounded, InfoRounded } from '@mui/icons-material'
import {
  Badge,
  Box,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material'
import { CustomModalContext } from 'common/contexts/CustomModalContext'
import { getLocaleFromFullDate } from 'common/libs/fechas'
import { enqueueSnackbar } from 'notistack'
import { useContext } from 'react'
import { deleteHojaGastoLin } from '../lib/HojasGastoLinController'
import ExpenseModal from './ExpenseModal'

import { styled } from '@mui/material/styles'

const StyledBadge = styled(Badge)(() => ({
  '& .MuiBadge-badge': {
    right: 8,
    top: 8,
  },
}))

const ExpenseCard = ({
  data: linea,
  id_hoja,
  showButton = true,
  handleChangeState,
  fechaInicio,
  fechaFin,
  disableAll,
}) => {
  const { handleModal } = useContext(CustomModalContext)
  const handleClick = () => {
    handleModal({
      closable: true,
      content: (
        <ExpenseModal
          id_hoja={id_hoja}
          updateState={handleChangeState}
          lineInfo={linea}
          fechaInicio={fechaInicio}
          fechaFin={fechaFin}
        />
      ),
    })
  }

  const handleDeleteClick = async ({ id_hoja, id_lin }) => {
    try {
      await deleteHojaGastoLin({ id_hoja, id_lin })
      enqueueSnackbar('Línea borrada correctamente', { variant: 'success' })
      handleChangeState()
    } catch (error) {
      enqueueSnackbar('No se ha podido borrar la línea')
    }
  }

  return (
    <Card>
      <CardActionArea onClick={handleClick}>
        <CardContent sx={{ p: 0, mb: 1 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flexWrap: 'wrap',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                color: 'primary.contrastText',
                backgroundColor: 'primary.main',
                padding: 2,
              }}
            >
              <Typography variant="caption" fontWeight="bold">
                {linea.id_lin}
              </Typography>
              <Typography variant="body1" fontSize="1.1rem !important">
                {getLocaleFromFullDate(linea.fecha, false)}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                gap: 2,
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    fontSize: '1rem',
                  }}
                >
                  <Typography variant="caption" fontWeight="bold">
                    CONCEPTO
                  </Typography>
                  <Typography variant="body1"> {linea.d_concepto}</Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    fontSize: '1rem',
                  }}
                >
                  <Typography variant="caption" fontWeight="bold">
                    DESCRIPCIÓN
                  </Typography>
                  <Typography variant="body1"> {linea.descripcion || ''}</Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  fontSize: '1rem',
                  padding: 2,
                  gap: 2,
                  minWidth: '160px',
                  textAlign: 'end',
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      fontSize: '1rem',
                    }}
                  >
                    <Typography
                      variant="caption"
                      fontWeight="bold"
                      textAlign="end"
                      sx={{ width: '100%' }}
                    >
                      CANTIDAD
                    </Typography>
                    <Typography variant="body1" textAlign="end" sx={{ width: '100%' }}>
                      {linea.cantidad}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      fontSize: '1rem',
                    }}
                  >
                    <Typography
                      variant="caption"
                      fontWeight="bold"
                      textAlign="end"
                      sx={{ width: '100%' }}
                    >
                      PRECIO
                    </Typography>
                    <Typography variant="body1" textAlign="end" sx={{ width: '100%' }}>
                      {linea.precio}
                    </Typography>
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    width: '100%',
                  }}
                >
                  <Typography
                    variant="caption"
                    fontWeight="bold"
                    textAlign="end"
                    sx={{ width: '100%' }}
                  >
                    IMPORTE
                  </Typography>
                  <Typography
                    variant="body1"
                    fontSize="3rem !important"
                    textAlign="end"
                    sx={{ width: '100%' }}
                  >
                    {linea.importe_interno}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </CardActionArea>
      <Divider />
      <CardActions sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
          {linea.observaciones ? (
            <Tooltip
              title={
                <Box>
                  <Typography variant="body1">Observaciones</Typography>
                  {linea.observaciones}
                </Box>
              }
            >
              <IconButton>
                <InfoRounded />
              </IconButton>
            </Tooltip>
          ) : (
            <></>
          )}
          {linea.documentos.length > 0 ? (
            <StyledBadge
              badgeContent={linea.documentos.length}
              color="primary"
              sx={{ top: 1, right: 1 }}
            >
              <Tooltip
                title={
                  <Box>
                    <Typography variant="body1">Documentos adjuntos</Typography>
                    {linea.documentos.map((doc, index) => (
                      <Typography key={doc.id || index} variant="body1" align="end">
                        {doc.descripcion}
                      </Typography>
                    ))}
                  </Box>
                }
              >
                <IconButton>
                  <AttachFile />
                </IconButton>
              </Tooltip>
            </StyledBadge>
          ) : (
            <></>
          )}
        </Box>
        {showButton && !disableAll ? (
          <IconButton
            color="error"
            onClick={() => handleDeleteClick({ id_hoja: linea.id_hoja, id_lin: linea.id_lin })}
          >
            <DeleteRounded />
          </IconButton>
        ) : null}
      </CardActions>
    </Card>
  )
}

export default ExpenseCard
